/**
 * ACCORDION MODULE
 * Handles FAQ and collapsible content functionality
 * Features: Smooth height animations, keyboard navigation, accessibility
 */

(function() {
  'use strict';

  window.AccordionModule = {
    accordions: [],

    /**
     * Initialize accordion
     * @param {HTMLElement} element - Accordion container
     */
    init: function(element) {
      if (!element) return;

      const accordion = {
        element: element,
        items: element.querySelectorAll('[data-accordion-item]'),
      };

      this.accordions.push(accordion);
      this.setupEventListeners(accordion);
    },

    /**
     * Setup event listeners for accordion items
     */
    setupEventListeners: function(accordion) {
      accordion.items.forEach((item) => {
        const trigger = item.querySelector('[data-accordion-trigger]');
        const content = item.querySelector('[data-accordion-content]');

        if (!trigger || !content) return;

        trigger.addEventListener('click', () => {
          this.toggle(item);
        });

        // Keyboard navigation
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggle(item);
          }

          // Arrow down: open next accordion or focus next trigger
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextItem = item.nextElementSibling;
            if (nextItem && nextItem.querySelector('[data-accordion-trigger]')) {
              nextItem.querySelector('[data-accordion-trigger]').focus();
            }
          }

          // Arrow up: focus previous trigger
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prevItem = item.previousElementSibling;
            if (prevItem && prevItem.querySelector('[data-accordion-trigger]')) {
              prevItem.querySelector('[data-accordion-trigger]').focus();
            }
          }
        });
      });
    },

    /**
     * Toggle accordion item
     */
    toggle: function(item) {
      const trigger = item.querySelector('[data-accordion-trigger]');
      const content = item.querySelector('[data-accordion-content]');
      const isOpen = item.classList.contains('open');

      if (isOpen) {
        this.close(item);
      } else {
        this.open(item);
      }
    },

    /**
     * Open accordion item with smooth height animation
     */
    open: function(item) {
      const trigger = item.querySelector('[data-accordion-trigger]');
      const content = item.querySelector('[data-accordion-content]');

      if (!content) return;

      item.classList.add('open');
      trigger.setAttribute('aria-expanded', 'true');
      content.setAttribute('aria-hidden', 'false');

      // Animate height
      const startHeight = 0;
      const endHeight = content.scrollHeight;
      this.animateHeight(content, startHeight, endHeight);
    },

    /**
     * Close accordion item with smooth height animation
     */
    close: function(item) {
      const trigger = item.querySelector('[data-accordion-trigger]');
      const content = item.querySelector('[data-accordion-content]');

      if (!content) return;

      item.classList.remove('open');
      trigger.setAttribute('aria-expanded', 'false');
      content.setAttribute('aria-hidden', 'true');

      // Animate height
      const startHeight = content.scrollHeight;
      const endHeight = 0;
      this.animateHeight(content, startHeight, endHeight, () => {
        content.style.height = '0';
      });
    },

    /**
     * Animate height change
     */
    animateHeight: function(element, startHeight, endHeight, callback) {
      const duration = 300;
      const startTime = performance.now();

      element.style.height = startHeight + 'px';
      element.style.overflow = 'hidden';
      element.style.transition = `height ${duration}ms ease`;

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const currentHeight = startHeight + (endHeight - startHeight) * progress;
        element.style.height = currentHeight + 'px';

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          element.style.height = endHeight + 'px';
          if (callback) callback();
        }
      };

      requestAnimationFrame(animate);
    },
  };

  // Auto-initialize accordions with data-accordion attribute
  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('[data-accordion]').forEach(element => {
      window.AccordionModule.init(element);
    });
  });
})();
